import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { msSinceOpen } from '../utils/openTiming';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonButton,
  IonIcon,
  IonContent,
  IonSearchbar,
  IonRefresherContent,
  IonList,
  IonItem,
  IonLabel,
  IonSkeletonText,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonAvatar,
  IonChip,
  IonFab,
  IonFabButton,
  IonModal,
} from '@ionic/react';
import { trash, call, chatbubble, mail, closeOutline, swapHorizontal, searchOutline, personAddOutline } from 'ionicons/icons';
import { ContactListItem, Label } from '@rsmaids/shared/types/contacts';
import styles from './ContactsListModal.module.scss';
import SheetModal from './SheetModal';
import NewContact from '../pages/bookingSystem/NewContact';
import PositionedRefresher from './PositionedRefresher';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { getCachedContactList } from '@/utils/contactCache';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import applyOverlayPosition from '../utils/overlayPosition';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

interface ContactsListModalProps {
  onClose: () => void;
  onSelectContact: (contact: ContactListItem) => void;
  title?: string; // Custom title, defaults to "Select Contact"
  searchPlaceholder?: string; // Custom search placeholder, defaults to "Search contacts..."
  showAnonymousOption?: boolean; // Show "Send to Anonymous" option when no results
  isOpen?: boolean; // Control modal open/closed state
  /**
   * Build the list while the modal is still closed. In React, Ionic only builds a modal's contents
   * once it starts presenting, so the panel slides in empty (a black screen) and the list is built
   * during the slide. With this on, the contents are already built and ready when it opens. Costs
   * the list being built when the host mounts, so only hosts that open it often (the Inbox) set it.
   */
  keepContentsMounted?: boolean;
  /**
   * Slider-button actions. When a host provides these, the Call/Message/Email
   * swipe buttons on each contact row route through the host's own in-place
   * mechanism (e.g. the SoftPhone switching to its keypad/Messages tab, or the
   * Inbox opening the conversation it already owns) instead of the default
   * behavior. `channel` is 'sms' for Message, 'email' for Email;
   * `fromEmail` is 'support' for Email. Omit all three to keep the default
   * (Call → rs:openDialer event; Message/Email → no-op host handling).
   */
  onContactCall?: (contact: ContactListItem) => void;
  onContactMessage?: (contact: ContactListItem, channel: 'sms' | 'email', fromEmail?: 'scheduling' | 'billing' | 'support') => void;
  /**
   * When provided, the "add contact" FAB calls this instead of opening the
   * NewContact form in a nested IonModal. Hosts that render this component
   * inside another modal (e.g. SoftPhone) MUST pass this — a 3rd stacked
   * IonModal renders behind the overlay backdrop (black screen). The host
   * should present NewContact as a sibling of this modal.
   */
  onAddContact?: () => void;
}

const ContactsListModal: React.FC<ContactsListModalProps> = ({
  onClose,
  onSelectContact,
  title = i18n.t('components.ContactsListModal.selectContact'),
  isOpen = true,
  keepContentsMounted = false,
  showAnonymousOption = false,
  onContactCall,
  onContactMessage,
  onAddContact,
}) => {
  const { t: tr } = useTranslation();
  const contactService = DataServiceFactory.getService();
  const ionContentRef = useRef<HTMLIonContentElement | null>(null);
  const contentRef = useRef<HTMLIonGridElement>(null);
  const titleCardRef = useRef<HTMLDivElement>(null);
  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const loadCachedContactsSync = (): ContactListItem[] => {
    try {
      const cached = localStorage.getItem('cachedContacts');
      if (!cached) return [];
      return (JSON.parse(cached) as ContactListItem[]).sort((a, b) =>
        (`${a.firstName || ''} ${a.lastName || ''}`.trim() || a.primaryEmail || '')
          .localeCompare(`${b.firstName || ''} ${b.lastName || ''}`.trim() || b.primaryEmail || '')
      );
    } catch { return []; }
  };
  const [contacts, setContacts] = useState<ContactListItem[]>(loadCachedContactsSync);
  const [filteredContacts, setFilteredContacts] = useState<ContactListItem[]>(loadCachedContactsSync);
  // Captured once via a lazy initializer (not a plain Date.now() call during
  // render) — this codebase's recency cutoffs don't need live-ticking
  // precision, and this matches how filteredContacts only re-derives on
  // input changes already, not on a timer.
  const [mountedAtMs] = useState(() => Date.now());

  // Keep a pre-sorted contacts array to avoid sorting during render
  const sortContacts = (list: ContactListItem[]) => {
    return [...list].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  };

  // Filter state - always starts clear
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedLabelId, setSelectedLabelId] = useState('');

  const [isLoading, setIsLoading] = useState(() => !localStorage.getItem('cachedContacts'));
  const [hasLoadedOnce, setHasLoadedOnce] = useState(() => !!localStorage.getItem('cachedContacts'));
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [availableLabels, setAvailableLabels] = useState<Label[]>([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const fabRef = useRef<HTMLElement | null>(null);

  // Filter options with counts — memoized so this isn't rebuilt (recomputing
  // counts and rebuilding closures for every option) on every render, only
  // when contacts/availableLabels actually change.
  const filterMenuOptions = useMemo(() => {
    const oneWeekAgo = mountedAtMs - (7 * 24 * 60 * 60 * 1000);

    const recentContactsCount = contacts.filter(c => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const lastActivity = (c as any).lastActivity;
      return lastActivity && new Date(lastActivity).getTime() > oneWeekAgo;
    }).length;

    const emailSubscribersCount = contacts.filter(c => c.emailSubscribed).length;
    const smsSubscribersCount = contacts.filter(c => c.smsSubscribed).length;

    const clientTypeCounts: { [key: string]: number } = {};
    contacts.forEach(c => {
      const clientType = c.contactfilter?.[0]?.clientType;
      if (Array.isArray(clientType)) {
        clientType.forEach(type => {
          clientTypeCounts[type] = (clientTypeCounts[type] || 0) + 1;
        });
      }
    });

    const baseOptions = [
      {
        label: tr('components.ContactsListModal.allContacts'),
        value: 'all',
        count: contacts.length,
        chipColor: 'primary',
        handler: () => {
          setActiveFilter('all');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
      {
        label: tr('components.ContactsListModal.recentContacts'),
        value: 'recent',
        count: recentContactsCount,
        chipColor: 'success',
        handler: () => {
          setActiveFilter('recent');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
      {
        label: tr('components.ContactsListModal.emailSubscribers'),
        value: 'emailSubscribed',
        count: emailSubscribersCount,
        chipColor: 'secondary',
        handler: () => {
          setActiveFilter('emailSubscribed');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
      {
        label: tr('components.ContactsListModal.smsSubscribers'),
        value: 'smsSubscribed',
        count: smsSubscribersCount,
        chipColor: 'tertiary',
        handler: () => {
          setActiveFilter('smsSubscribed');
          setSelectedLabelId('');
          setIsFilterModalOpen(false);
        }
      },
    ];

    const clientTypeOptions = Object.entries(clientTypeCounts).map(([type, count]) => ({
      label: type.charAt(0).toUpperCase() + type.slice(1),
      value: type,
      count,
      chipColor: 'medium' as const,
      handler: () => {
        setActiveFilter(type);
        setSelectedLabelId('');
        setIsFilterModalOpen(false);
      }
    }));

    const labelOptions = availableLabels.map(label => ({
      label: label.name,
      value: label.id || '',
      count: contacts.filter(c => c.labelIds?.includes(label.id || '')).length,
      chipColor: 'warning' as const,
      handler: () => {
        setActiveFilter('label');
        setSelectedLabelId(label.id || '');
        setIsFilterModalOpen(false);
      }
    }));

    return [...baseOptions, ...clientTypeOptions, ...labelOptions];
  }, [contacts, availableLabels, mountedAtMs, tr]);


  // Handle scroll for header animation (matches Conversation.tsx pattern)
  // Prefer Ionic onIonScroll + rAF throttling (more reliable & testable)
  const rafTick = useRef(false);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const wasAtTop = useRef(true);
  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    const headerEl = document.querySelector<HTMLElement>('[data-header-title]');

    if (smooth) {
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardAnimationRef.current) {
        titleCardAnimationRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      }
      setTimeout(() => {
        if (headerEl) headerEl.style.transition = '';
        if (titleCardAnimationRef.current) titleCardAnimationRef.current.style.transition = '';
      }, 220);
    }

    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));

    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentRef, 66, applyProgress);

  const handleContactClick = (contact: ContactListItem) => {
    onSelectContact(contact);
    onClose();
  };

  const handleAddContact = useCallback(() => {
    if (onAddContact) { onAddContact(); return; }
    setShowNewContactModal(true);
  }, [onAddContact]);

  // Compute horizontal position for search overlay (vertical is CSS-driven)
  const updateOverlayPosition = useCallback(() => {
    try {
      applyOverlayPosition(fabRef.current, overlayRef.current, isSearchVisible);
    } catch { }
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  // Re-position overlay horizontally when expanded state changes — and every time the modal opens:
  // the overlay is a fresh DOM element then (the modal's contents are rebuilt on each open), and
  // nothing else repositions it, so it stayed wherever the animation CSS left it. Run again after
  // the modal's own enter animation, when the overlay is actually laid out.
  useEffect(() => {
    if (!isOpen) return;
    const ids = [30, 350].map(ms => window.setTimeout(() => updateOverlayPosition(), ms));
    return () => ids.forEach(id => window.clearTimeout(id));
  }, [isOpen, isSearchVisible, updateOverlayPosition]);


  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  // (the overlay is repositioned on open as well — see the effect above)

  // Keyboard tracking using translate3d animation (matches ContactsList.tsx pattern)
  useEffect(() => {
    let bottomAnimRaf = 0;
    let currentBottom = 0;
    let didShowHandle: { remove: () => Promise<void> } | null = null;
    let didHideHandle: { remove: () => Promise<void> } | null = null;

    const getKeyboardHeightFallback = () => {
      const vv = window.visualViewport;
      if (!vv) return 0;
      const offset = window.innerHeight - vv.height - vv.offsetTop;
      return offset > 50 ? offset : 0;
    };

    const setBottomOffset = (offset: number) => {
      const elements = [fabRef.current, overlayRef.current].filter(Boolean) as HTMLElement[];
      if (offset > 0) {
        const bottomPx = `${offset}px`;
        elements.forEach(el => {
          el.style.setProperty('bottom', bottomPx, 'important');
          el.style.removeProperty('transform');
        });
      } else {
        elements.forEach(el => {
          el.style.removeProperty('bottom');
          el.style.removeProperty('transform');
        });
      }
    };

    const animateBottomTo = (kbHeight: number, forceHide = false) => {
      const effectiveHeight = forceHide ? 0 : (kbHeight > 0 ? kbHeight : getKeyboardHeightFallback());
      const targetBottom = effectiveHeight > 0 ? effectiveHeight + KEYBOARD_GAP_PX : 0;

      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);

        if (t < 1) {
          bottomAnimRaf = requestAnimationFrame(step);
        } else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };

      bottomAnimRaf = requestAnimationFrame(step);
    };

    try {
      Keyboard.addListener('keyboardWillShow', (info) => {
        animateBottomTo(info.keyboardHeight || 0);
      }).then(handle => { didShowHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidShow', (info) => {
        animateBottomTo(info.keyboardHeight || 0);
      }).catch(() => { });

      Keyboard.addListener('keyboardWillHide', () => {
        animateBottomTo(0, true);
      }).then(handle => { didHideHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidHide', () => {
        animateBottomTo(0, true);
      }).catch(() => { });
    } catch { }

    // Fallback: visualViewport-based keyboard detection.
    // Keep this enabled for Android too because some device/WebView combinations
    // report delayed or zero keyboardHeight in plugin callbacks.
    const vv = window.visualViewport;
    let vvRaf = 0;
    const handleVVResize = () => {
      if (vvRaf) cancelAnimationFrame(vvRaf);
      vvRaf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        const kbHeight = offset > 50 ? offset : 0;
        animateBottomTo(kbHeight);
      });
    };
    if (vv) vv.addEventListener('resize', handleVVResize);

    // Re-sync position when app regains focus (e.g. after screen lock/unlock).
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setTimeout(handleVVResize, 300);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      [fabRef.current, overlayRef.current].forEach(el => {
        if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      });
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (vvRaf) cancelAnimationFrame(vvRaf);
      if (vv) vv.removeEventListener('resize', handleVVResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { didShowHandle?.remove(); } catch { }
      try { didHideHandle?.remove(); } catch { }
    };
  }, []);

  const handleToggleSearch = useCallback(() => {
    if (isSearchVisible) {
      // Clear input immediately
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
      }
      setSearchText('');
      setFilteredContacts(contacts);
      // Start closing animation
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220); // Match animation duration

      // Blur input and hide keyboard
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) {
          getter().then((inputEl: HTMLInputElement) => {
            try { inputEl.blur(); } catch { }
            try { Keyboard.hide(); } catch { }
          }).catch(() => { try { Keyboard.hide(); } catch { } });
        } else {
          try { Keyboard.hide(); } catch { }
        }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);

      // Focus input after expansion animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          updateOverlayPosition();
          try {
            const s = searchRef.current as HTMLIonSearchbarElement | null;
            const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
            if (getter) {
              getter().then((inputEl: HTMLInputElement) => {
                try { inputEl.focus(); } catch { }
              }).catch(() => { });
            }
          } catch { }
        });
      });
    }
  }, [isSearchVisible, contacts, updateOverlayPosition]);

  // Show the spinner immediately when reopening without a cache — computed
  // during render (React's "adjust state when a prop changes" pattern)
  // rather than as a synchronous setState call at the top of the effect,
  // which now only handles the actual async load.
  const [wasContactsModalOpen, setWasContactsModalOpen] = useState(isOpen);
  if (isOpen !== wasContactsModalOpen) {
    setWasContactsModalOpen(isOpen);
    // Every open (and close) starts clean. This component stays mounted while the modal is closed,
    // so the last session's search text, filter, label and the search bar's open / closing state
    // all carried over — the list opened already filtered, with the search bar half-way through
    // its animation.
    setSearchText('');
    setActiveFilter('all');
    setSelectedLabelId('');
    setIsSearchVisible(false);
    setIsSearchClosing(false);
    setIsSearchHidden(false);
    setVisibleCount(50);
    if (isOpen && !localStorage.getItem('cachedContacts')) setIsLoading(true);
  }

  useEffect(() => {
    // Only load data when modal is open
    if (!isOpen) return;

    let isMounted = true;

    const finishLoading = () => {
      if (isMounted) {
        setIsLoading(false);
        setHasLoadedOnce(true);
      }
    };

    const initializeData = async () => {
      try {
        const cachedLabelsJson = localStorage.getItem('cachedLabels');
        let processedLabels: Label[] = [];
        if (cachedLabelsJson) {
          try { processedLabels = JSON.parse(cachedLabelsJson); } catch { }
        }

        // Always show from cache immediately — ContactsList is responsible for keeping cache fresh.
        // Only hit the network if there is no cache at all (first install).
        const contactsResult = await getCachedContactList(() => contactService.contacts.getAllContacts());
        const processedContacts = contactsResult.data;

        if (isMounted) {
          const sorted = sortContacts(processedContacts);
          setContacts(sorted);
          setFilteredContacts(sorted);
          setAvailableLabels(processedLabels);
          finishLoading();
        }
      } catch (error) {
        console.error('Error initializing contacts:', error);
        if (isMounted) finishLoading();
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, [isOpen, contactService.contacts]);

  // Re-filters contacts whenever any input changes — computed during
  // render (React's "adjust state when a dependency changes" pattern)
  // rather than as setState calls inside an effect, since it's a pure
  // derivation from already-available state with no external system
  // involved.
  const [prevFilterInputs, setPrevFilterInputs] = useState({ searchText, activeFilter, selectedLabelId, contacts, hasLoadedOnce });
  if (
    prevFilterInputs.searchText !== searchText ||
    prevFilterInputs.activeFilter !== activeFilter ||
    prevFilterInputs.selectedLabelId !== selectedLabelId ||
    prevFilterInputs.contacts !== contacts ||
    prevFilterInputs.hasLoadedOnce !== hasLoadedOnce
  ) {
    setPrevFilterInputs({ searchText, activeFilter, selectedLabelId, contacts, hasLoadedOnce });
    if (hasLoadedOnce) {
      let filtered = [...contacts];

      if (searchText) {
        const searchLower = searchText.toLowerCase();
        filtered = filtered.filter(contact => {
          const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.toLowerCase();
          const email = (contact.primaryEmail || '').toLowerCase();
          const phone = (contact.primaryPhone || '').toLowerCase();
          return fullName.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
        });
      }

      if (activeFilter !== 'all') {
        const oneWeekAgo = mountedAtMs - (7 * 24 * 60 * 60 * 1000);

        if (activeFilter === 'recent') {
          filtered = filtered.filter(c => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const lastActivity = (c as any).lastActivity;
            return lastActivity && new Date(lastActivity).getTime() > oneWeekAgo;
          });
        } else if (activeFilter === 'emailSubscribed') {
          filtered = filtered.filter(c => c.emailSubscribed);
        } else if (activeFilter === 'smsSubscribed') {
          filtered = filtered.filter(c => c.smsSubscribed);
        } else if (activeFilter === 'label' && selectedLabelId) {
          filtered = filtered.filter(c => c.labelIds?.includes(selectedLabelId));
        } else {
          filtered = filtered.filter(c => {
            const clientType = c.contactfilter?.[0]?.clientType;
            return Array.isArray(clientType) && clientType.includes(activeFilter);
          });
        }
      }

      filtered.sort((a, b) => {
        const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.primaryEmail || '';
        const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.primaryEmail || '';
        return nameA.localeCompare(nameB);
      });

      setFilteredContacts(filtered);
      setVisibleCount(50);
    }
  }

  const handleRefresh = async (event: CustomEvent) => {
    try {
      setIsLoading(true);

      const [contactsResult, labelsResult] = await Promise.allSettled([
        contactService.contacts.getAllContacts(),
        contactService.labels.getAllLabels()
      ]);

      if (contactsResult.status === 'fulfilled') {
        const allEmails = contactsResult.value.map(c => c.primaryEmail).filter(Boolean);
        const allPhones = contactsResult.value.map(c => c.primaryPhone).filter(Boolean);

        const subscriptionResult = await contactService.contacts.getBulkSubscriptionStatuses(allEmails, allPhones);

        const processedContacts = contactsResult.value.map((contact: ContactListItem) => ({
          ...contact,
          labels: contact.labels || [],
          labelIds: contact.labelIds || [],
          displayName: contact.displayName ||
            `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
            (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : tr('components.ContactsListModal.unknownContact')),
          emailSubscribed: contact.primaryEmail ? subscriptionResult.emailSubscriptions.get(contact.primaryEmail) || false : false,
          smsSubscribed: contact.primaryPhone ? subscriptionResult.smsSubscriptions.get(contact.primaryPhone) || false : false,
        }));

        const sorted = sortContacts(processedContacts);
        setContacts(sorted);
        setFilteredContacts(sorted);
        localStorage.setItem('cachedContacts', JSON.stringify(processedContacts));
        localStorage.setItem('contactsLastLoadTime', Date.now().toString());
      }

      if (labelsResult.status === 'fulfilled') {
        setAvailableLabels(labelsResult.value);
      }
    } catch (error) {
      console.error('Error refreshing contacts:', error);
    } finally {
      setIsLoading(false);
      event.detail.complete();
    }
  };

  // Call slider button: hand off to the host if it wants to keep the action
  // in-place (SoftPhone → keypad tab); otherwise open the in-app SoftPhone
  // dialer on the keypad tab with this contact's number prefilled. No auto-dial.
  const handleCall = useCallback((contact: ContactListItem, e: React.MouseEvent<HTMLIonItemOptionElement, MouseEvent>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (onContactCall) {
      onContactCall(contact);
      return;
    }
    const phone = contact.primaryPhone || '';
    if (phone) {
      const contactName =
        contact.displayName ||
        `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
        undefined;
      window.dispatchEvent(new CustomEvent('rs:openDialer', { detail: { phone, intent: 'keypad', contactName } }));
    }
  }, [onContactCall]);

  const handleDeleteClick = useCallback(async (contact: ContactListItem, e?: React.MouseEvent): Promise<void> => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!contact.id) {
      console.error('Cannot delete contact without an ID');
      alert(tr('components.ContactsListModal.failedToDeleteContactNoId'));
      return;
    }

    if (window.confirm(tr('components.ContactsListModal.areYouSureYouWantTo', { firstName: contact.firstName, lastName: contact.lastName }))) {
      try {
        await contactService.contacts.deleteContact(contact.id);
        const updatedContacts = contacts.filter((c: ContactListItem) => c.id !== contact.id);
        const sorted = sortContacts(updatedContacts);
        setContacts(sorted);
        setFilteredContacts(sorted);

        localStorage.setItem('cachedContacts', JSON.stringify(updatedContacts));
      } catch (error) {
        console.error('Error deleting contact:', error);
        alert(tr('components.ContactsListModal.failedToDeleteContactPleaseTry'));
      }
    }
  }, [contacts, contactService.contacts, tr]);

  // Message / Email slider buttons: hand off to the host, which opens the
  // conversation for this contact in-place (SoftPhone → Messages tab; Inbox
  // picker → the conversation view it already owns). Message = SMS channel,
  // Email = email channel with the Support account preselected. No-op if the
  // host didn't provide a handler.
  const handleMessage = useCallback((contact: ContactListItem, e: React.MouseEvent<HTMLIonItemOptionElement, MouseEvent>): void => {
    e.preventDefault();
    e.stopPropagation();
    onContactMessage?.(contact, 'sms');
  }, [onContactMessage]);

  const handleEmail = useCallback((contact: ContactListItem, e: React.MouseEvent<HTMLIonItemOptionElement, MouseEvent>): void => {
    e.preventDefault();
    e.stopPropagation();
    onContactMessage?.(contact, 'email', 'support');
  }, [onContactMessage]);

  const handleClearSearch = useCallback(() => {
    // Batch state updates to avoid race condition
    const sortedContacts = [...contacts].sort((a, b) => {
      const nameA = `${a.firstName || ''} ${a.lastName || ''}`.trim() || a.primaryEmail || '';
      const nameB = `${b.firstName || ''} ${b.lastName || ''}`.trim() || b.primaryEmail || '';
      return nameA.localeCompare(nameB);
    });

    // Clear all filters and set contacts directly to bypass useEffect filter logic
    setSearchText('');
    setActiveFilter('all');
    setSelectedLabelId('');

    // Force update filtered contacts immediately
    setFilteredContacts(sortedContacts);

    if (isFilterModalOpen) {
      setIsFilterModalOpen(false);
    }
  }, [contacts, isFilterModalOpen]);

  const getFilterDisplayName = (filterType: string): string => {
    if (filterType === 'all') return tr('components.ContactsListModal.allContacts');
    if (filterType === 'recent') return tr('components.ContactsListModal.recentContacts');
    if (filterType === 'label' && selectedLabelId) {
      const label = availableLabels.find(l => l.id === selectedLabelId);
      return label ? label.name : tr('components.ContactsListModal.selectedLabel');
    }
    return tr('components.ContactsListModal.allContacts');
  };

  // Group contacts into letter-based buckets so the list renders as
  // separate cards per letter instead of one continuous list (matches
  // ContactsList page).
  const getGroupLetter = (contact: ContactListItem): string => {
    const name = contact.displayName ||
      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
      (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : '');
    const first = name.trim().charAt(0).toUpperCase();
    return /[A-Z]/.test(first) ? first : '#';
  };

  const getDisplayNameForSort = (contact: ContactListItem): string =>
    contact.displayName ||
    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
    (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : '');

  // Timing: when the content / list is actually on screen, relative to the tap
  useEffect(() => {
    if (isOpen) console.log(`⏱️ [PERF] Picker: content rendered ${msSinceOpen(0)}ms after the tap (loading=${isLoading}, contacts=${contacts.length}, filtered=${filteredContacts.length})`);
  }, [isOpen, isLoading, contacts.length, filteredContacts.length]);

  const visibleContacts = filteredContacts.slice(0, visibleCount);
  const groupMap = new Map<string, ContactListItem[]>();
  visibleContacts.forEach(contact => {
    const letter = getGroupLetter(contact);
    const bucket = groupMap.get(letter);
    if (bucket) {
      bucket.push(contact);
    } else {
      groupMap.set(letter, [contact]);
    }
  });

  const contactGroups = Array.from(groupMap.entries())
    .map(([letter, items]) => ({
      letter,
      items: [...items].sort((a, b) => getDisplayNameForSort(a).localeCompare(getDisplayNameForSort(b))),
    }))
    .sort((a, b) => {
      if (a.letter === '#') return 1;
      if (b.letter === '#') return -1;
      return a.letter.localeCompare(b.letter);
    });

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      backdropDismiss={true}
      keepContentsMounted={keepContentsMounted}
      onWillPresent={() => console.log(`⏱️ [PERF] Picker: modal starts presenting ${msSinceOpen(0)}ms after the tap`)}
      onDidPresent={() => console.log(`⏱️ [PERF] Picker: modal finished presenting ${msSinceOpen(0)}ms after the tap`)}
    >
      <div className={styles.modalWrapper}>
        <SheetModal
          isOpen={isFilterModalOpen}
          onClose={() => {
            setIsFilterModalOpen(false);
          }}
          title={tr('components.ContactsListModal.filterContacts')}
          showChips={true}
          menuOptions={filterMenuOptions.map(option => ({
            ...option,
            onClick: option.handler,
            labelContent: (
              <IonItem lines="none" className={styles.filterItem}>
                <IonLabel>{option.label}</IonLabel>
                <IonChip color={option.chipColor} slot="end">
                  <IonLabel>{option.count}</IonLabel>
                </IonChip>
              </IonItem>
            )
          }))}
        />
        <BasePageWithHeader
          ref={baseHeaderRef}
          title={title}
          toolbarClassName={styles.toolbar}
          smallTitleClassName={styles.smallTitle}
          titleHeaderClassName={styles.titleHeader}
          startButtons={
            <IonButton shape="round" className={styles.closeButton} onClick={onClose}>
              <IonIcon icon={closeOutline} />
            </IonButton>
          }
          endButtons={
            <IonButton
              shape="round"
              className={styles.searchButton}
              onClick={handleToggleSearch}
            >
              <IonIcon icon={isSearchVisible ? closeOutline : searchOutline} />
            </IonButton>
          }
        >
          <IonContent scrollY={false} ref={ionContentRef} className={styles.content}>
            <PositionedRefresher
              titleRef={titleCardRef}
              contentRef={contentRef}
              offset={16}
              anchor="bottom"
              id="contact-refresher"
              className={styles.contactsRefresher}
              pullMin={120}
              pullMax={200}
              pullFactor={0.6}
              disabled={refresherDisabled}
              onIonRefresh={handleRefresh}
              style={{
                '--ion-color-step-250': 'var(--ion-background-color)',
                '--ion-color-step-200': 'var(--ion-background-color)',
                '--ion-background-color': 'transparent',
              } as React.CSSProperties}
            >
              <IonRefresherContent
                pullingIcon="bubbles"
                refreshingSpinner="bubbles"
              />
            </PositionedRefresher>
            <IonGrid
              ref={contentRef}
              className={styles.contactsContainer}
              onScroll={(e) => {
                const el = e.target as HTMLElement;
                const top = el.scrollTop || 0;
                const atTop = top <= 5;
                if (atTop !== wasAtTop.current) {
                  wasAtTop.current = atTop;
                  setRefresherDisabled(!atTop);
                }
                if (!rafTick.current) {
                  rafTick.current = true;
                  requestAnimationFrame(() => {
                    handleScroll(top);
                    rafTick.current = false;
                  });
                }
                const deltaFromBottom = el.scrollHeight - top - el.clientHeight;
                if (deltaFromBottom < 200) {
                  setVisibleCount(v => v < filteredContacts.length ? v + 50 : v);
                }
              }}
            >
              <div ref={titleCardRef} className={styles.titleCardWrapper}>
                <TitleCard
                  ref={titleCardAnimationRef}
                  title={title}
                  cardClassName={styles.contactsTitleCard}
                  titleContainerClassName={styles.titleContainer}
                  titleClassName={styles.contactsTitle}
                />
              </div>
              {isLoading ? (
                <IonList className={styles.skeletonOverlay}>
                  {Array.from({ length: 15 }, (_, index) => (
                    <IonItem key={index} lines="full">
                      <IonSkeletonText animated slot="start" style={{ width: '40px', height: '40px', borderRadius: '50%' }} />
                      <IonLabel>
                        <h3><IonSkeletonText animated style={{ width: '70%', height: '20px' }} /></h3>
                      </IonLabel>
                      <IonSkeletonText animated slot="end" style={{ width: '60px', height: '20px' }} />
                    </IonItem>
                  ))}
                </IonList>
              ) : contacts.length === 0 && hasLoadedOnce ? (
                <IonGrid className={styles.emptyState}>
                  <IonRow>
                    <IonCol className="ion-text-center">
                      <IonList role="list">
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText className="ion-text-large">{tr('components.ContactsListModal.noContactsFound')}
                            </IonText>
                          </IonLabel>
                        </IonItem>
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText>{tr('components.ContactsListModal.tryAddingANewContact')}</IonText>
                          </IonLabel>
                        </IonItem>
                      </IonList>
                      <IonButton onClick={handleAddContact}>{tr('components.ContactsListModal.addContact')}
                      </IonButton>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              ) : contacts.length > 0 && filteredContacts.length === 0 && hasLoadedOnce ? (
                <IonGrid className={styles.emptyState}>
                  <IonRow>
                    <IonCol className="ion-text-center">
                      <IonList role="list">
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText className="ion-text-large">{tr('components.ContactsListModal.noContactsFound')}
                              {searchText && (<span>{tr('components.ContactsListModal.for', { searchText })}</span>)}
                              {activeFilter !== 'all' && (<span>{tr('components.ContactsListModal.in')} {getFilterDisplayName(activeFilter)}</span>)}
                            </IonText>
                          </IonLabel>
                        </IonItem>
                        <IonItem lines="none" className="ion-text-center" role="listitem">
                          <IonLabel color="medium" className="ion-text-wrap">
                            <IonText>{tr('components.ContactsListModal.tryAdjustingYourSearchOrFilters')}</IonText>
                          </IonLabel>
                        </IonItem>
                      </IonList>
                      {(searchText || activeFilter !== 'all') && (
                        <IonButton onClick={handleClearSearch}>{tr('components.ContactsListModal.clearSearch')}
                        </IonButton>
                      )}
                      {showAnonymousOption && searchText && (
                        <IonButton
                          expand="block"
                          fill="outline"
                          onClick={() => {
                            const anonymousContact: ContactListItem = {
                              id: 'anonymous-' + Date.now(),
                              firstName: 'Anonymous',
                              lastName: '',
                              displayName: searchText,
                              primaryEmail: searchText.includes('@') ? searchText : '',
                              primaryPhone: searchText.match(/^\+?[\d\s()-]+$/) ? searchText : '',
                              emailSubscribed: false,
                              smsSubscribed: false,
                              labels: []
                            };
                            onSelectContact(anonymousContact);

                            setSearchText('');
                            onClose();
                          }}
                          style={{ marginTop: '16px' }}
                        >{tr('components.ContactsListModal.sendToAnonymous', { searchText })}</IonButton>
                      )}
                    </IonCol>
                  </IonRow>
                </IonGrid>
              ) : filteredContacts.length > 0 ? (
                <div className={styles.letterGroups}>
                  {contactGroups.map((group) => (
                    <div key={group.letter} className={styles.letterGroup}>
                      <IonText className={styles.letterHeading}>{group.letter}</IonText>
                      <IonList className={styles.contactList}>
                        {group.items.map(contact => (
                          <IonItemSliding
                            key={contact.id}
                          >
                            <IonItemOptions side="start">
                              <IonItemOption color="danger" expandable onClick={(e) => handleDeleteClick(contact, e)} className={styles.smallOptionText}>
                                <IonIcon slot="bottom" icon={trash}></IonIcon>{tr('components.ContactsListModal.delete')}
                              </IonItemOption>
                            </IonItemOptions>

                            <IonItem button detail={false} onClick={() => handleContactClick(contact)} className={styles.solidItem} role="listitem">
                              <IonAvatar slot="start" className={styles.avatar}>
                                <img
                                  src={proxyImage(contact.avatar ?? '') || getDefaultAvatar(
                                    contact.displayName ||
                                    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                                    (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : 'Contact')
                                  )}
                                  alt={contact.displayName || `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || contact.primaryEmail || tr('components.ContactsListModal.contact')}
                                  onError={(e) => {
                                    const imgElement = e.target as HTMLImageElement;

                                    imgElement.src = getDefaultAvatar(
                                      contact.displayName ||
                                      `${contact.firstName || ''} ${contact.lastName || ''}`.trim() ||
                                      (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : 'Contact')
                                    );
                                  }}
                                />
                              </IonAvatar>

                              <IonLabel>
                                {contact.displayName ||
                                  (contact.firstName || contact.lastName ?
                                    `${contact.firstName || ''} ${contact.lastName || ''}`.trim() :
                                    (contact.primaryEmail ? contact.primaryEmail.split('@')[0] : tr('components.ContactsListModal.unknownContact'))
                                  )
                                }
                              </IonLabel>
                              {(() => {
                                const { clientType } = contact.contactfilter?.[0] || {};
                                return Array.isArray(clientType) && clientType.includes('members') && (
                                  <IonChip className={styles.memberChip} slot="end">
                                    <IonLabel>{tr('components.ContactsListModal.member')}</IonLabel>
                                  </IonChip>
                                );
                              })()}
                              <IonIcon
                                slot="end"
                                icon={swapHorizontal}
                                color="medium"
                                size="small"
                                className={styles.slideIndicator}
                                aria-label={tr('components.ContactsListModal.swipeForOptions')}
                              />
                            </IonItem>

                            <IonItemOptions side="end">
                              <IonItemOption color="primary" onClick={(e) => handleCall(contact, e)} className={styles.smallOptionText}>
                                <IonIcon slot="bottom" icon={call}></IonIcon>{tr('components.ContactsListModal.call')}
                              </IonItemOption>
                              {/* Message/Email need a host that opens the conversation in-place
                                  (SoftPhone, Inbox picker). Hidden where no such handler is wired. */}
                              {onContactMessage && (
                                <IonItemOption color="secondary" onClick={(e) => handleMessage(contact, e)} className={styles.smallOptionText}>
                                  <IonIcon slot="bottom" icon={chatbubble}></IonIcon>{tr('components.ContactsListModal.message')}
                                </IonItemOption>
                              )}
                              {onContactMessage && (
                                <IonItemOption color="tertiary" onClick={(e) => handleEmail(contact, e)} className={styles.smallOptionText}>
                                  <IonIcon slot="bottom" icon={mail}></IonIcon>{tr('components.ContactsListModal.email')}
                                </IonItemOption>
                              )}
                            </IonItemOptions>
                          </IonItemSliding>
                        ))}
                      </IonList>
                    </div>
                  ))}
                </div>
              ) : null}
            </IonGrid>
          </IonContent>

          <IonFab ref={el => { fabRef.current = el as unknown as HTMLElement; }} className={`${styles.calendarFab} ${styles.calendarFabNoTabs}`}>
            <IonFabButton
              size='small'
              onClick={() => {
                if (isSearchVisible) {
                  handleToggleSearch();
                } else {
                  handleAddContact();
                }
              }}
              aria-label={isSearchVisible ? tr('components.ContactsListModal.closeSearch') : tr('components.ContactsListModal.addNewContact')}
            >
              <IonIcon icon={isSearchVisible ? closeOutline : personAddOutline} />
            </IonFabButton>
          </IonFab>

          {/* Non-invasive overlay: centered pop-out; does NOT change FAB styles */}
          <IonFab ref={el => { overlayRef.current = el as unknown as HTMLElement; }} className={`${styles.searchMorph} ${styles.searchMorphNoTabs} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`} aria-hidden={isSearchVisible ? 'false' : 'true'}>
            <IonSearchbar
              ref={el => { searchRef.current = el as unknown as HTMLIonSearchbarElement | null; }}
              value={searchText}
              debounce={50}
              onIonFocus={() => {
                requestAnimationFrame(() => updateOverlayPosition());
              }}
              onIonInput={e => setSearchText(e.detail.value ?? '')}
              onIonClear={() => {
                // Clear the search text and reset filtered results immediately.
                setFilteredContacts(contacts);
                setSearchText('');
                setActiveFilter('all');
                setSelectedLabelId('');
              }}
              placeholder={tr('components.ContactsListModal.searchContacts', { length: contacts.length })}
              animated
              showCancelButton="never"
              showClearButton={searchText ? 'always' : 'never'}
              className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
            />
          </IonFab>
        </BasePageWithHeader>

        {/* Fallback in-place NewContact modal for hosts that DON'T pass
            onAddContact. Hosts that render this component nested inside another
            modal must pass onAddContact and present NewContact as a sibling —
            a 3rd stacked IonModal renders behind the backdrop (black screen). */}
        {showNewContactModal && (
          <IonModal
            isOpen={showNewContactModal}
            onDidDismiss={() => setShowNewContactModal(false)}
          >
            <NewContact
              onClose={() => setShowNewContactModal(false)}
            />
          </IonModal>
        )}
      </div>
    </IonModal>
  );
};

export default React.memo(ContactsListModal);
